import queryString from "query-string";
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router";

interface ParsedFilters {
  [key: string]: unknown
}

// Overload signatures for better type safety
export function useUrlFilter<T>(field: string, defaultValue: T): [T, (value: T | undefined) => void];
export function useUrlFilter<T>(field: string): [T | undefined, (value: T | undefined) => void];
export function useUrlFilter<T = any>(field: string, defaultValue?: T): [T | undefined, (value: T | undefined) => void] {
  const [selected, setSelected] = useState<T | undefined>(defaultValue)
  const location = useLocation()
  const history = useHistory()

  useEffect(() => {
    const queryStrings = queryString.parse(location.search)
    let parsedFilters: ParsedFilters = {}

    if (queryStrings.filters && typeof queryStrings.filters === 'string') {
      try {
        parsedFilters = JSON.parse(queryStrings.filters)
      } catch (error) {
        console.warn('Failed to parse filters from URL:', error)
        parsedFilters = {}
      }
    }

    // Use the URL value if it exists, otherwise use the default value
    const urlValue = parsedFilters[field] as T | undefined
    setSelected(urlValue !== undefined ? urlValue : defaultValue)
  }, [location.search, field, defaultValue])

  const setUrlFilter = (value: T | undefined) => {
    const queryStrings = queryString.parse(location.search)
    let parsedFilters: ParsedFilters = {}

    if (queryStrings.filters && typeof queryStrings.filters === 'string') {
      try {
        parsedFilters = JSON.parse(queryStrings.filters)
      } catch (error) {
        console.warn('Failed to parse filters from URL:', error)
        parsedFilters = {}
      }
    }

    if (value !== undefined) {
      parsedFilters[field] = value
    } else {
      delete parsedFilters[field]
    }

    const filtersString = JSON.stringify(parsedFilters)
    const modifiedQueryStrings: queryString.ParsedQuery<string> = {
      ...queryStrings
    }

    if (filtersString === "{}") {
      delete modifiedQueryStrings.filters
    } else {
      modifiedQueryStrings.filters = filtersString
    }

    setSelected(parsedFilters[field] as T | undefined || defaultValue)
    history.replace({
      pathname: location.pathname,
      search: queryString.stringify(modifiedQueryStrings, { encode: false })
    })
  }

  return [selected, setUrlFilter]
}
